// Recovery from stale lazy chunks after a deploy (#618).
// Pages serves HTML with max-age=0 and /assets/* as immutable hashed files, so
// only a tab opened BEFORE a deploy can ask for a chunk that no longer exists.
// Pure helpers are kept free of window/document so node:test can cover them.

export const RELOAD_KEY = 'e4:chunk-reload-at';
export const RELOAD_WINDOW_MS = 60_000;

// Browser wordings for a failed dynamic import / preload:
//  Chrome/Edge: "Failed to fetch dynamically imported module: <url>"
//  Firefox:     "error loading dynamically imported module: <url>"
//  Safari:      "Importing a module script failed."
//  Vite:        "Unable to preload CSS for <dep>"
//  webpack-style: "Loading chunk N failed" / ChunkLoadError
const CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
  /loading (css )?chunk [\w-]+ failed/i,
];

export function isChunkLoadError(error) {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;
  const message = typeof error === 'string' ? error : error.message;
  return typeof message === 'string' && CHUNK_PATTERNS.some((re) => re.test(message));
}

// Decide whether to reload now. At most one reload per windowMs; records the
// attempt. If storage is unavailable the loop guard cannot work, so do not reload.
export function shouldReloadNow(storage, now = Date.now(), windowMs = RELOAD_WINDOW_MS) {
  try {
    const last = Number(storage.getItem(RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < windowMs) return false;
    storage.setItem(RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

function safeSessionStorage(win) {
  try {
    return win.sessionStorage;
  } catch {
    return null;
  }
}

// Reload (once per window) and report whether a reload was triggered.
export function reloadOnceForNewBuild(win = window) {
  const storage = safeSessionStorage(win);
  if (storage && shouldReloadNow(storage)) {
    console.warn('[chunk-recovery] reloading once to pick up the new build');
    win.location.reload();
    return true;
  }
  return false;
}

// Vite fires `vite:preloadError` when a preload (CSS or modulepreload dependency)
// fails and rethrows unless the event is default-prevented. Reload once to pick
// up the new build; on a repeat within the window, leave the event alone so the
// error reaches React.lazy and the error boundary shows the message.
export function installChunkRecovery(win = window) {
  win.addEventListener('vite:preloadError', (event) => {
    if (!isChunkLoadError(event.payload)) return;
    if (reloadOnceForNewBuild(win)) event.preventDefault();
  });
}
